import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  PHONE_VERIFICATION_STATUS,
  START_PHONE_VERIFICATION,
} from '../lib/graphql/mutations/customer';

export type PhoneVerificationPurpose = 'LOGIN' | 'PASSWORD_RESET';
export type PhoneVerificationStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'EXPIRED'
  | 'CONSUMED';

export interface PhoneVerificationSession {
  sessionId: string;
  phone: string;
  status: PhoneVerificationStatus;
  smsUri?: string | null;
  displayInstruction?: string | null;
  expiresAt?: string | null;
  verifiedAt?: string | null;
}

const POLL_INTERVAL_MS = 3000; // verify.mn docs: do not poll faster than 3s

export const usePhoneVerification = () => {
  const [session, setSession] = useState<PhoneVerificationSession | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<PhoneVerificationSession | null>(null);
  const expiredRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopPolling();
    },
    [stopPolling],
  );

  const checkStatus = useCallback(async () => {
    const current = sessionRef.current;
    if (!current || current.status === 'VERIFIED' || current.status === 'CONSUMED') {
      stopPolling();
      return;
    }

    try {
      const { default: apollo } = await import('../lib/apollo-client');
      const { data } = await apollo.query({
        query: PHONE_VERIFICATION_STATUS,
        variables: { sessionId: current.sessionId },
        fetchPolicy: 'no-cache',
      });

      const remote = data?.customerPhoneVerificationStatus;
      if (!remote) return;

      const next: PhoneVerificationSession = {
        ...current,
        status: remote.status,
        verifiedAt: remote.verifiedAt,
      };

      sessionRef.current = next;
      setSession(next);

      if (remote.status === 'VERIFIED' || remote.status === 'CONSUMED') {
        stopPolling();
      } else if (remote.status === 'EXPIRED') {
        stopPolling();
        expiredRef.current = true;
      }
    } catch (err) {
      // Network blips while polling are non-fatal; the next tick retries.
      console.warn('Phone verification poll failed:', err);
    }
  }, [stopPolling]);

  const start = useCallback(
    async (phone: string, purpose: PhoneVerificationPurpose) => {
      setStarting(true);
      setError(null);
      stopPolling();
      expiredRef.current = false;

      try {
        const { default: apollo } = await import('../lib/apollo-client');
        const { data } = await apollo.mutate({
          mutation: START_PHONE_VERIFICATION,
          variables: { phone: phone.trim(), purpose },
        });

        const created = data?.customerStartPhoneVerification;
        if (!created?.sessionId) {
          throw new Error('Баталгаажуулалт үүсгэж чадсангүй');
        }

        const next: PhoneVerificationSession = {
          sessionId: created.sessionId,
          phone: created.phone ?? phone.trim(),
          status: created.status ?? 'PENDING',
          smsUri: created.smsUri,
          displayInstruction: created.displayInstruction,
          expiresAt: created.expiresAt,
        };

        sessionRef.current = next;
        setSession(next);

        stopPolling();
        pollRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);

        return next;
      } catch (err) {
        const message =
          err instanceof ApolloError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Алдаа гарлаа';
        setError(message);
        return null;
      } finally {
        setStarting(false);
      }
    },
    [checkStatus, stopPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    sessionRef.current = null;
    expiredRef.current = false;
    setSession(null);
    setError(null);
  }, [stopPolling]);

  return {
    session,
    starting,
    error,
    expired: expiredRef.current,
    start,
    reset,
    checkStatus,
  };
};

export default usePhoneVerification;
